import { useMemo } from 'react';
import { Control, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { z } from 'zod';
import {
  runsCustomerApp,
  type TenantListItem,
  type TenantType,
  type TenantUserAdminListItem
} from '@bliks-io/contracts';
import { FormInput, FormMultiSelect, FormSelect, FormCheckbox } from '@/components/form/rhf';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  FieldSeparator,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label
} from '@bliks-io/ui';
import {
  getUserTypeOptions,
  getUserClassificationOptions,
  getUserStatusLabel,
  getUserStatusColorScheme
} from '@/lib/user-utils';
import { useEmailChangeWarning } from '@/hooks';
import type { AddTenantUserFormData } from './AddTenantUserDialog';

interface AddTenantUserFormFieldsProps {
  control: Control<AddTenantUserFormData>;
  tenantId?: string;
  /** Type of the tenant the member belongs to; undefined until one is known. */
  tenantType?: TenantType;
  tenantsData?: { tenants: TenantListItem[] };
  isLoadingTenants: boolean;
  isExternal: boolean;
  isEditMode: boolean;
  editingUser?: TenantUserAdminListItem;
  onTenantChange: (value: string) => void;
  onClassificationChange: (value: string | undefined) => void;
}

const tenantOptionLabel = (tenant: TenantListItem) => `${tenant.name} (${tenant.slug})`;

export function AddTenantUserFormFields({
  control,
  tenantId,
  tenantType,
  tenantsData,
  isLoadingTenants,
  isExternal,
  isEditMode,
  editingUser,
  onTenantChange,
  onClassificationChange
}: AddTenantUserFormFieldsProps) {
  const { t } = useTranslation();
  const classificationOptions = getUserClassificationOptions(t);
  const storedUserTypes = editingUser?.userTypes;
  // A member may already hold a role this tenant's branch no longer offers.
  // Re-admitting it is what makes such a member repairable — an option that is
  // not rendered cannot be deselected, while its value still rides along in
  // every submit.
  const userTypeOptions = useMemo(
    () => (tenantType ? getUserTypeOptions(t, tenantType, storedUserTypes) : []),
    [t, tenantType, storedUserTypes]
  );
  // A partner tenant's members are its own staff, and the partner area grants
  // by role alone — so classification is not a choice there. Both controls mean
  // different things per branch, so neither renders until the branch is known:
  // a control shown against the wrong branch collects a value the form then has
  // to undo.
  const showsClassification = tenantType !== undefined && runsCustomerApp(tenantType);

  const emailChangeWarning = useEmailChangeWarning(control, editingUser?.email);

  // RHF's dirty tracking is the same source of truth the dialog's
  // dirty-subset PATCH extraction uses, so warning and payload can't drift.
  const { dirtyFields } = useFormState({ control, name: 'homeOrgTenantId' });
  const homeOrgChanged = Boolean(dirtyFields.homeOrgTenantId);
  const currentHomeOrgTenantId = editingUser?.homeOrgTenantId;
  const eligibleHomeOrgTenantIds = editingUser?.eligibleHomeOrgTenantIds;
  const homeOrgTargetOptions = useMemo(() => {
    // The backend already narrowed eligibleHomeOrgTenantIds to the complete
    // set of valid move targets (eligible membership + SSO tenant), so this is
    // a plain lookup for labels — no invalid choice can be offered.
    const eligibleTenantIds = new Set(eligibleHomeOrgTenantIds ?? []);
    const options = (tenantsData?.tenants ?? [])
      .filter((tenant) => eligibleTenantIds.has(tenant._id))
      .map((tenant) => ({ value: tenant._id, label: tenantOptionLabel(tenant) }));
    // The current pin is absent from the targets whenever it is no longer a
    // valid target (membership revoked, staff/disabled identity, tenant
    // flipped to legacy). Re-add it so the admin sees the real pin instead of
    // "not set" and can un-dirty an accidental change.
    if (currentHomeOrgTenantId && !options.some((o) => o.value === currentHomeOrgTenantId)) {
      const current = tenantsData?.tenants.find((tenant) => tenant._id === currentHomeOrgTenantId);
      if (current) {
        options.unshift({ value: current._id, label: tenantOptionLabel(current) });
      }
    }
    return options;
  }, [tenantsData?.tenants, currentHomeOrgTenantId, eligibleHomeOrgTenantIds]);

  return (
    <div className="flex flex-col gap-8">
      {/* Tenant Selection - only shown when tenantId is not pre-set */}
      {!tenantId && (
        <div className="space-y-2">
          <Label htmlFor="tenantId">{t('tenantAdmin.tenantName')}</Label>
          {isLoadingTenants ? (
            <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
          ) : (
            <Select onValueChange={onTenantChange}>
              <SelectTrigger id="tenantId">
                <SelectValue placeholder={t('tenantAdmin.tenantTypePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {tenantsData?.tenants.map((tenant) => (
                  <SelectItem key={tenant._id} value={tenant._id}>
                    {tenantOptionLabel(tenant)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Basic Information */}
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormInput
            control={control}
            name="firstName"
            label={t('common.FirstName')}
            placeholder={t('organization.members.firstNamePlaceholder')}
            required
            rules={{ required: t('errors.validation.required') }}
          />
          <FormInput
            control={control}
            name="lastName"
            label={t('common.LastName')}
            placeholder={t('organization.members.lastNamePlaceholder')}
            required
            rules={{ required: t('errors.validation.required') }}
          />
        </div>

        <FormInput
          control={control}
          name="email"
          label={t('common.Email')}
          placeholder={t('organization.members.emailPlaceholder')}
          type="email"
          required
          rules={{
            pattern: { value: z.regexes.email, message: t('errors.validation.email') }
          }}
          description={emailChangeWarning}
        />
      </div>

      {/* Classification */}
      {showsClassification && (
        <div className="flex flex-col gap-4">
          <FieldSeparator>{t('common.Classification')}</FieldSeparator>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormSelect
              control={control}
              name="classification"
              label={t('common.Classification')}
              placeholder={t('organization.members.selectClassification')}
              options={classificationOptions}
              onValueChange={onClassificationChange}
              required
            />
            {isExternal && (
              <FormInput
                control={control}
                name="externalOrganization"
                label={t('organization.members.externalOrganization')}
                placeholder={t('organization.members.externalOrgPlaceholder')}
                required
              />
            )}
          </div>
        </div>
      )}

      {/* Access & Permissions */}
      {tenantType !== undefined && (
        <div className="flex flex-col gap-4">
          <FieldSeparator>{t('organization.members.accessPermissions')}</FieldSeparator>

          {!isEditMode ? (
            <FormMultiSelect
              control={control}
              name="userTypes"
              label={t('common.UserTypes')}
              placeholder={t('organization.members.selectUserTypes')}
              items={userTypeOptions}
              selectedTextFormatter={(count) => t('common.itemsSelected', { count })}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_minmax(0,0.43fr)]">
              <FormMultiSelect
                control={control}
                name="userTypes"
                label={t('common.UserTypes')}
                placeholder={t('organization.members.selectUserTypes')}
                items={userTypeOptions}
                selectedTextFormatter={(count) => t('common.itemsSelected', { count })}
              />
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">{t('common.Status')}</label>
                <Badge
                  colorScheme={getUserStatusColorScheme(editingUser?.status)}
                  className="h-10 px-3 text-sm flex items-center justify-center w-full"
                >
                  {getUserStatusLabel(editingUser?.status, t)}
                </Badge>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Home organization - superadmin move, edit mode only */}
      {isEditMode && (
        <div className="flex flex-col gap-4">
          <FieldSeparator>{t('tenantAdmin.homeOrgLabel')}</FieldSeparator>
          {editingUser?.homeOrgDeleted && (
            <Alert colorScheme="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t('tenantAdmin.homeOrgDeletedAlertTitle')}</AlertTitle>
              <AlertDescription>
                {/* "Select below" would contradict an empty dropdown — with no
                    eligible target, point at the prerequisite instead. While
                    tenants load the target list is transiently empty, so keep
                    the default copy until loading settles. */}
                {!isLoadingTenants && homeOrgTargetOptions.length === 0
                  ? t('tenantAdmin.homeOrgDeletedNoTargetsAlert')
                  : t('tenantAdmin.homeOrgDeletedAlert')}
              </AlertDescription>
            </Alert>
          )}
          <FormSelect
            control={control}
            name="homeOrgTenantId"
            label={t('tenantAdmin.homeOrgLabel')}
            placeholder={t('tenantAdmin.homeOrgNotSet')}
            options={homeOrgTargetOptions}
          />
          {!isLoadingTenants && homeOrgTargetOptions.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('tenantAdmin.homeOrgNoTargets')}</p>
          )}
          {homeOrgChanged && (
            <Alert colorScheme="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t('tenantAdmin.homeOrgMoveWarningTitle')}</AlertTitle>
              <AlertDescription>{t('tenantAdmin.homeOrgMoveWarning')}</AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Send Invitation - only in create mode */}
      {!isEditMode && (
        <div className="flex flex-col gap-4">
          <FieldSeparator>{t('organization.members.statusActions')}</FieldSeparator>
          <label className="relative w-full rounded-lg border p-4 bg-background hover:bg-accent/5 transition-colors cursor-pointer block">
            <FormCheckbox
              control={control}
              name="sendInvitation"
              label={t('organization.members.sendInvitation')}
              description={t('organization.members.sendInvitationHelper')}
            />
          </label>
        </div>
      )}
    </div>
  );
}
